import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnChanges,
  Output,
  type SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  ServiceOfferingShopCategory,
  type ServiceOfferingShopCategoryService,
  type ServiceOfferingWithOwnerDetails,
} from '@freelancer/datastore/collections';
import { trackById, trackByValue } from '@freelancer/ui';
import { CarouselScrollBehaviour } from '@freelancer/ui/carousel';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { generateImageSizes } from '@freelancer/ui/picture';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import { type FormGroupTypeOf, isDefined } from '@freelancer/utils';
import {
  type CarouselHeaderFormType,
  type CategorySaveEvent,
  type CategoryServiceActionOptions,
  defaultActionOptions,
} from './service-offering-carousel.types';

@Component({
  selector: 'fl-service-offering-carousel',
  template: `
    <div
      class="ServiceOfferingCarouselContainer"
      flTrackingSection="{{ trackingSection }}"
    >
      <fl-service-offering-carousel-header
        [category]="category"
        [isEditMode]="isCategoryEditMode"
        [form]="categoryForm"
        [namePlaceholder]="categoryNamePlaceholder"
        [descriptionPlaceholder]="categoryDescriptionPlaceholder"
        [isLoading]="isLoading"
        [flMarginBottom]="'xxsmall'"
        [flMarginBottomDesktop]="'small'"
        (save)="saveCategory.emit($event)"
        (cancel)="exitEditCategory.emit($event)"
      />
      <fl-carousel
        class="ServiceOfferingCarousel"
        [noIndex]="true"
        [arrows]="false"
        [arrowsDesktop]="
          isServiceEditMode
            ? services?.length + 1 >= slidesToShowDesktop
            : services?.length >= slidesToShowDesktop
        "
        [arrowOffsetDesktop]="'-26px'"
        [padding]="8"
        [showPeekSlide]="showPeekSlide"
        [peekSlideWidth]="peekSlideWidth"
        [slidesToShow]="slidesToShow"
        [slidesToShowTablet]="slidesToShowTablet"
        [slidesToShowDesktop]="slidesToShowDesktop"
        [slidesToShowDesktopLarge]="slidesToShowDesktopLarge"
        [slidesToShowDesktopXLarge]="slidesToShowDesktopXLarge"
        [slidesToShowDesktopXXLarge]="slidesToShowDesktopXXLarge"
        [scrollBehaviour]="CarouselScrollBehaviour.FREE_AND_INDIVIDUAL"
        [flMarginBottom]="'mid'"
      >
        <ng-container *ngIf="isLoading; else notLoading">
          <fl-carousel-item
            *ngFor="let _ of [1, 2, 3, 4, 5]; trackBy: trackByValue"
          >
            <fl-service-offering-carousel-card [isLoading]="isLoading" />
          </fl-carousel-item>
        </ng-container>
        <ng-template #notLoading>
          <ng-container
            *ngIf="
              isShopCategoryServiceArray;
              else servicesWithOwnerDetailsArray
            "
          >
            <fl-carousel-item
              *ngFor="let service of shopCategoryServices; trackBy: trackById"
            >
              <fl-service-offering-carousel-card
                [imageSizes]="imageSizes"
                [isEditMode]="isServiceEditMode"
                [isLoading]="isLoading"
                [service]="service"
                [serviceActionOptions]="serviceActionOptions"
                [useSmallerPriceSize]="useSmallerPriceSize"
                (editService)="editService.emit($event)"
                (removeService)="removeService.emit($event)"
              />
            </fl-carousel-item>
          </ng-container>
          <ng-template #servicesWithOwnerDetailsArray>
            <fl-carousel-item
              *ngFor="
                let service of servicesWithOwnerDetails;
                trackBy: trackById
              "
            >
              <fl-service-offering-carousel-card
                [hideUsername]="hideUsername"
                [imageSizes]="imageSizes"
                [isEditMode]="isServiceEditMode"
                [isLoading]="isLoading"
                [fromUrl]="fromUrl"
                [service]="service"
                [serviceActionOptions]="serviceActionOptions"
                [useSmallerPriceSize]="useSmallerPriceSize"
                (editService)="editService.emit($event)"
                (removeService)="removeService.emit($event)"
              />
            </fl-carousel-item>
          </ng-template>
          <fl-carousel-item *ngIf="isServiceEditMode">
            <fl-service-offering-carousel-card
              [imageSizes]="imageSizes"
              [isEditMode]="true"
              (addService)="addService.emit()"
            />
          </fl-carousel-item>
          <fl-carousel-item *ngIf="showSeeMoreCard">
            <fl-service-offering-carousel-card [showSeeMoreCard]="true" />
          </fl-carousel-item>
          <fl-carousel-item *ngIf="emptyCategory && !isServiceEditMode">
            <fl-service-offering-carousel-card [emptyCard]="true" />
          </fl-carousel-item>
        </ng-template>
      </fl-carousel>
    </div>
  `,
  styleUrls: ['./service-offering-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselComponent implements OnChanges {
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  FontColor = FontColor;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  TextSize = TextSize;
  trackById = trackById;
  trackByValue = trackByValue;

  @Input() category?: ServiceOfferingShopCategory;
  @Input() services:
    | readonly ServiceOfferingShopCategoryService[]
    | readonly ServiceOfferingWithOwnerDetails[] = [];
  @Input() categoryForm?: FormGroup<FormGroupTypeOf<CarouselHeaderFormType>>;
  @Input() categoryNamePlaceholder = $localize`Name your shop category here`;
  @Input()
  categoryDescriptionPlaceholder = $localize`Describe your shop category here`;
  /** Sets category name and description to be input fields */
  @Input() isCategoryEditMode: boolean;
  /** Sets the carousel to a loading state. This will override the header and cards. */
  @Input() isLoading: boolean;
  /** Sets service card to be editable. Possible actions depend on serviceActionOptions */
  @Input() isServiceEditMode: boolean;
  /** Determine what service-related actions can be done */
  @Input() serviceActionOptions: CategoryServiceActionOptions =
    defaultActionOptions;
  /** Number of slides visible on mobile to larger screen sizes */
  @Input() slidesToShow = 2;
  /** Number of slides visible on tablet to larger screen sizes */
  @Input() slidesToShowTablet = 3;
  /** Number of slides visible on desktop to larger screen sizes */
  @Input() slidesToShowDesktop = 5;
  /** Number of slides visible on desktop large to larger screen sizes */
  @Input() slidesToShowDesktopLarge?: number;
  /** Number of slides visible on desktop xlarge to larger screen sizes */
  @Input() slidesToShowDesktopXLarge?: number;
  /** Number of slides visible on desktop xxlarge to larger screen sizes */
  @Input() slidesToShowDesktopXXLarge?: number;
  /** Tracking Section to use. Defaults to `ServiceOfferingCarousel`. */
  @Input() trackingSection = 'ServiceOfferingCarousel';
  /** Use a smaller price size font */
  @Input() useSmallerPriceSize = false;

  /**
   * Determines if a portion of the next slide should be visible.
   * This is applied only when CarouselScrollBehaviour is FREE.
   * If true, a part of the nth slide (where n is the number of slides to show) will be visible.
   */
  @Input() showPeekSlide = true;
  /**
   * Defines the width of the peeking slide as a fraction of the slide width.
   * Values should be between 0 to 1. By default, 50% (0.5) of the peeking slide is visible.
   */
  @Input() peekSlideWidth = 0.3;
  /**
   * Hides the usernames shown on the service offering cards.
   * Only works when passing in `ServiceOfferingWithOwnerDetails`.
   */
  @Input() hideUsername = false;
  @Input() fromUrl?: string;
  /**
   * Toggles the See More card at the end of the carousel.
   * Used for linking to the Services Landing Page.
   */
  @Input() showSeeMoreCard = false;

  @Output() addService = new EventEmitter<void>();
  @Output() editService =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() removeService =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() toggleServiceVisibility =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() saveCategory = new EventEmitter<CategorySaveEvent>();
  @Output() exitEditCategory = new EventEmitter<
    ServiceOfferingShopCategory | undefined
  >();

  emptyCategory = false;
  isShopCategoryServiceArray = false;
  shopCategoryServices: readonly ServiceOfferingShopCategoryService[] = [];
  servicesWithOwnerDetails: readonly ServiceOfferingWithOwnerDetails[] = [];
  imageSizes: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.services && this.services?.length > 0) {
      if (this.isShopCategoryServiceType(this.services[0])) {
        this.isShopCategoryServiceArray = true;
        this.shopCategoryServices = this
          .services as ServiceOfferingShopCategoryService[];
        this.servicesWithOwnerDetails = [];
      } else {
        this.isShopCategoryServiceArray = false;
        this.servicesWithOwnerDetails = this
          .services as ServiceOfferingWithOwnerDetails[];
        this.shopCategoryServices = [];
      }
    }

    if (changes.services && this.services?.length === 0 && !this.isLoading) {
      this.emptyCategory = true;
    }

    if (
      ('slidesToShow' in changes &&
        changes.slidesToShow.currentValue !==
          changes.slidesToShow.previousValue) ||
      ('slidesToShowTablet' in changes &&
        changes.slidesToShowTablet.currentValue !==
          changes.slidesToShowTablet.previousValue) ||
      ('peekSlideWidth' in changes &&
        changes.peekSlideWidth.currentValue !==
          changes.peekSlideWidth.previousValue)
    ) {
      this.imageSizes = generateImageSizes({
        mobile: isDefined(this.slidesToShow)
          ? `${100 / (this.slidesToShow - this.peekSlideWidth)}vw`
          : undefined,
        tablet: isDefined(this.slidesToShowTablet)
          ? `${100 / (this.slidesToShowTablet - this.peekSlideWidth)}vw`
          : undefined,
        // Desktop devices usually have a pixel ratio of just 1, so we need to manually set
        // image size in order to avoid blurry images on larger screens.
        // These sizes are way larger than the actual container size (and sometimes even the screen size),
        // but the browser will automatically scale the image down to fit the container.
        desktopSmall: '1080px',
        defaultSize: '1200px', // this seems to be the acceptable default for larger screens in terms of image quality
      });
    }
  }

  isShopCategoryServiceType(
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): service is ServiceOfferingShopCategoryService {
    return 'categoryId' in service;
  }
}
